import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CircleAlert } from 'lucide-react';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

export function AlertsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get(`/warehouse/alerts?seller_id=${sellerId}&resolved=${showResolved}&limit=50&types=order_cancelled,shipment_cancelled,claim_opened`);
      if (res.success) setAlerts(res.data || []);
    } catch (err) { console.error('Failed to load alerts', err); }
    finally { setLoading(false); }
  }, [sellerId, showResolved]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id: number) => { await api.put('/warehouse/alerts/' + id + '/resolve', {}); load(); };

  if (loading) return <div className="py-8 text-center text-[var(--color-text-secondary)]">{t('common.loading')}</div>;

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
        <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
        {t('wh.showResolved')}
      </label>
      {alerts.length === 0 ? (
        <div className="text-center py-8 text-[var(--color-text-secondary)]">
          <Check size={40} className="mx-auto mb-3 text-green-400" /><p>{t('wh.noAlerts')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert: any) => (
            <div key={alert.id}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                alert.resolved ? 'bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700 opacity-60'
                  : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
              }`}>
              <CircleAlert size={18} className={alert.type?.includes('cancel') ? 'text-red-500' : 'text-yellow-500'} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--color-text)]">{alert.message}</p>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  Batch #{alert.seller_batch_num ?? alert.batch_id} -- {new Date(alert.created_at).toLocaleString()}
                </p>
              </div>
              {!alert.resolved && (
                <Can cap="warehouse.operate">
                  <button onClick={() => resolve(alert.id)}
                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300">
                    {t('wh.resolve')}
                  </button>
                </Can>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
